import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import api from "../api/client";
import { AuthContext } from "./AuthContext";
import useLiveNoticeRefresh from "../hooks/useLiveNoticeRefresh";
import { noticeReadOptions } from "../services/systemNoticeEvents";

const defaultAnnouncement = {
  message: "",
  type: "general",
  beaconsEnabled: false,
};

const SNOWBANK_STORAGE_KEY = "snowbank-effect-enabled";

const AnnouncementContext = createContext({
  announcement: defaultAnnouncement,
  updateAnnouncement: () => {},
  clearAnnouncement: () => {},
  refreshAnnouncement: () => {},
  snowbankEnabled: false,
  toggleSnowbank: () => {},
});

export function AnnouncementProvider({ children }) {
  const { user } = useContext(AuthContext);
  const [announcement, setAnnouncement] = useState(defaultAnnouncement);
  const [snowbankEnabled, setSnowbankEnabled] = useState(() => {
    if (typeof window === "undefined") return false;
    const saved = window.localStorage.getItem(SNOWBANK_STORAGE_KEY);
    return saved === "true";
  });

  const fetchAnnouncement = useCallback(async (signal) => {
    try {
      const res = await api.get("/system-settings/announcement", noticeReadOptions(signal));
      if (res.data && !signal.aborted) {
        const next = {
          message: res.data.message ?? "",
          type: res.data.type ?? "general",
          beaconsEnabled: res.data.beaconsEnabled ?? false,
        };
        // Keep object identity on unchanged polls: admin draft forms depend on
        // this object and must not be reset while someone is typing.
        setAnnouncement(previous => previous.message === next.message && previous.type === next.type &&
          previous.beaconsEnabled === next.beaconsEnabled ? previous : next);
      }
    } catch (error) {
      // Silently fail - user may not be authenticated yet
      console.warn("Failed to fetch announcement", error);
    }
  }, [user?.id]);

  const refreshAnnouncement = useLiveNoticeRefresh(fetchAnnouncement, !!user?.id);
  useEffect(() => { setAnnouncement(defaultAnnouncement); }, [user?.id]);

  // Reset snowbank when beacons are disabled
  useEffect(() => {
    if (!announcement.beaconsEnabled && snowbankEnabled) {
      setSnowbankEnabled(false);
      window.localStorage.setItem(SNOWBANK_STORAGE_KEY, "false");
    }
  }, [announcement.beaconsEnabled, snowbankEnabled]);

  const toggleSnowbank = useCallback(() => {
    // Only allow toggle when beacons are enabled
    if (!announcement.beaconsEnabled) return;
    setSnowbankEnabled((prev) => {
      const newValue = !prev;
      window.localStorage.setItem(SNOWBANK_STORAGE_KEY, String(newValue));
      return newValue;
    });
  }, [announcement.beaconsEnabled]);

  const value = useMemo(
    () => ({
      announcement,
      updateAnnouncement: async (update) => {
        try {
          const payload = {
            message: update?.message ?? "",
            type: update?.type ?? "general",
            beaconsEnabled: update?.beaconsEnabled ?? false,
          };
          await api.put("/system-settings/announcement", payload);
          await refreshAnnouncement();
        } catch (error) {
          console.error("Failed to update announcement", error);
          throw error;
        }
      },
      clearAnnouncement: async () => {
        try {
          await api.delete("/system-settings/announcement");
          await refreshAnnouncement();
        } catch (error) {
          console.error("Failed to clear announcement", error);
          throw error;
        }
      },
      refreshAnnouncement,
      snowbankEnabled: announcement.beaconsEnabled && snowbankEnabled,
      toggleSnowbank,
    }),
    [announcement, refreshAnnouncement, snowbankEnabled, toggleSnowbank]
  );

  return <AnnouncementContext.Provider value={value}>{children}</AnnouncementContext.Provider>;
}

export function useAnnouncement() {
  return useContext(AnnouncementContext);
}
